import { reactive } from 'vue';
import { io } from 'socket.io-client';
import useLeadsQuery from '@/composables/useLeadsQuery';
import useGlobalUrl from '@/composables/useGlobalUrl';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useReminderQuery from '@/composables/useReminderQuery';
import useCurrentUser from '@/composables/useCurrentUser';
import useLeadFilesQuery from '@/composables/useLeadFilesQuery';
import useLeadHistoryQuery from '@/composables/useLeadHistoryQuery';
import useCompaniesQuery from '@/composables/useCompaniesQuery';
import useContactsQuery from '@/composables/useContactsQuery';
import useUsersQuery from './composables/useUsersQuery';
import usePicklistItemsQuery from './composables/usePicklistItemsQuery';
import useSilentAccessToken from '@/composables/useSilentAccessToken';

const { user, getCurrentUser } = useCurrentUser();
const { getUsers } = useUsersQuery();
const { decryptSymbols } = useDecryptSymbols();
const { refetchLeadFiles, filesCache } = useLeadFilesQuery();
const { refetchLeadHistory, historyCache } = useLeadHistoryQuery();
const { pickLists } = usePicklistItemsQuery;

const { companies } = useCompaniesQuery;
const { contacts } = useContactsQuery;

export const state = reactive({
  connected: false,
});

const { rootUrl } = useGlobalUrl();

export const socket = io(rootUrl, {
  autoConnect: false,
});

export const initSocket = async () => {
  let token;

  try {
    const { getAccessToken } = useSilentAccessToken();
    token = await getAccessToken();
  } catch (err) {
    // Error handling is centralized in useSilentAccessToken
    return;
  }

  socket.auth = { token };

  socket.connect();
};

socket.on('connect', () => {
  state.connected = true;
  console.log('connected');
});

socket.on('disconnect', () => {
  state.connected = false;
  console.log('disconnected');
});

socket.on('updateLead', (lead) => {
  lead = decryptSymbols(lead);
  const index = useLeadsQuery.leads.findIndex((l) => l.LEADID === lead.LEADID);
  if (index !== -1) {
    useLeadsQuery.leads[index] = lead;
  } else {
    useLeadsQuery.leads.push(lead);
  }

  // Only refetch files and history if this client has previously loaded them
  if (filesCache.value.has(lead.LEADID)) {
    refetchLeadFiles(lead.LEADID);
  }

  if (historyCache.value.has(lead.LEADID)) {
    refetchLeadHistory(lead.LEADID);
  }
});

socket.on('deleteLead', (leadid) => {
  const index = useLeadsQuery.leads.findIndex((l) => l.LEADID === leadid);
  if (index !== -1) {
    useLeadsQuery.leads.splice(index, 1);
  }
});

socket.on('updateReminders', (userid) => {
  if (userid === user.userid) {
    useReminderQuery.refetchReminders.value();
  }
});

socket.on('updateCompany', (company) => {
  const index = companies.findIndex((c) => c.COMPANYID.toUpperCase() === company.COMPANYID.toUpperCase());
  if (index !== -1) {
    companies[index] = decryptSymbols(company);
  } else {
    companies.push(decryptSymbols(company));
  }
});

socket.on('deleteCompany', (companyid) => {
  const index = companies.findIndex((c) => c.COMPANYID.toUpperCase() === companyid.toUpperCase());
  if (index !== -1) {
    companies.splice(index, 1);
  }
});

socket.on('updateContact', (contact) => {
  const index = contacts.findIndex((c) => c.CONTACTID.toUpperCase() === contact.CONTACTID.toUpperCase());
  if (index !== -1) {
    contacts[index] = decryptSymbols(contact);
  } else {
    contacts.push(decryptSymbols(contact));
  }
});

socket.on('deleteContact', (contactid) => {
  const index = contacts.findIndex((c) => c.CONTACTID.toUpperCase() === contactid.toUpperCase());
  if (index !== -1) {
    contacts.splice(index, 1);
  }
});

socket.on('updateUsers', () => {
  getCurrentUser();
  getUsers();
});

socket.on('updatePickLists', ({ picklistid, items }) => {
  if (!picklistid || items === undefined) {
    return;
  }

  // Find the correct picklist by id and set its items to the new items array
  for (const key in pickLists) {
    if (pickLists[key].id.toUpperCase() === picklistid.toUpperCase()) {
      pickLists[key].items.splice(0, pickLists[key].items.length, ...decryptSymbols(items));
      break;
    }
  }
});
